import React, { useState, useEffect } from 'react';
import OverviewCard from './components/OverviewCard';
import { ViewsIcon, ProfitIcon, ProductIcon, UsersIcon } from './icons/OverviewIcons';
import PaymentsOverview from './charts/PaymentsOverview';
import WeeksProfit from './charts/WeeksProfit';
import apiClient from '../../services/apiClient';
import { useFavorites } from '../../context/FavoritesContext';

// Compact format helper
const compactFormat = (num) => {
  if (!num) return '0';
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return Math.round(num).toString();
};

const NextAdminDashboard = () => {
  const { favorites } = useFavorites();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/properties/dashboard-analytics');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Use fallback data
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics from real data
  const totalProperties = dashboardData?.overview?.totalProperties || 0;
  const avgPrice = dashboardData?.overview?.avgPrice || 0;
  const totalCounties = dashboardData?.overview?.totalCounties || 0;
  const favoriteCount = Array.isArray(favorites) ? favorites.length : 0;

  const overviewData = {
    views: {
      value: totalProperties,
      growthRate: 0.43, // Calculate from historical data
    },
    profit: {
      value: avgPrice,
      growthRate: 4.35,
    },
    products: {
      value: totalCounties,
      growthRate: 2.59,
    },
    users: {
      value: favoriteCount,
      growthRate: favoriteCount > 0 ? 12.5 : -0.95,
    },
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 2xl:p-10">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-dark dark:text-white">Dashboard</h1>
          <p className="text-dark-6">Loading foreclosure dealfinder data...</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-4 2xl:gap-7.5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-10 bg-white p-6 shadow-1 dark:bg-gray-dark animate-pulse">
              <div className="h-14 w-14 bg-gray-200 rounded-full"></div>
              <div className="mt-6 space-y-2">
                <div className="h-8 bg-gray-200 rounded w-20"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 2xl:p-10">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-dark dark:text-white">
          Dashboard
        </h1>
        <p className="text-dark-6">
          Foreclosure Dealfinder - NJ Property Analytics
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 xl:grid-cols-4 2xl:gap-7.5">
        <OverviewCard
          label="Total Properties"
          value={compactFormat(overviewData.views.value)}
          growthRate={overviewData.views.growthRate}
          Icon={ViewsIcon}
        />

        <OverviewCard
          label="Avg Property Value"
          value={'$' + compactFormat(overviewData.profit.value)}
          growthRate={overviewData.profit.growthRate}
          Icon={ProfitIcon}
        />

        <OverviewCard
          label="Total Counties"
          value={compactFormat(overviewData.products.value)}
          growthRate={overviewData.products.growthRate}
          Icon={ProductIcon}
        />

        <OverviewCard
          label="Your Favorites"
          value={compactFormat(overviewData.users.value)}
          growthRate={overviewData.users.growthRate}
          Icon={UsersIcon}
        />
      </div>

      {/* Charts */}
      <div className="mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6 2xl:mt-9 2xl:gap-7.5">
        <PaymentsOverview 
          className="col-span-12 xl:col-span-7"
          dashboardData={dashboardData}
        />
        <WeeksProfit 
          className="col-span-12 xl:col-span-5"
          dashboardData={dashboardData}
        />
      </div>
    </div>
  );
};

export default NextAdminDashboard;
